import React, { useState, useEffect } from "react";
import { Box, makeStyles, withStyles, Tabs, Tab, Typography, IconButton, Tooltip } from "@material-ui/core";
import _ from "lodash";
import { useHistory } from "react-router-dom";
import { useTranslate } from "react-polyglot";
import FormatIndentIncreaseIcon from "@material-ui/icons/FormatIndentDecrease";
import Theory, { makeTheoryTreeItems } from "./Theory.jsx";
import WordsLearning from "./WordsLearning.jsx";
import Videos from "./Videos.jsx";
import { useCourseProvider } from "../../../Providers/Data/CourseProvider.jsx";
import { useLearnProcess } from "../../../Providers/Data/LearnProcessProvider.jsx";
import Exercises from "./Exercises/Exercises.jsx";
import useExtractRouteParamInt from "../../../hooks/useExtractRouteParamInt.js";
import useEffectWithPreviousValues from "../../../hooks/useEffectWithPreviousValues.js";
import questionIcon from "../../../assets/writing-white.png";
import { IS_APP_BEHAPPY, largeRewardItemTypes } from "../../../constants.js";
import { useDialogs } from "../../../Providers/Common/DialogsProvider.jsx";
import ExercisesTreeByTags from "./Exercises/Components/ExercisesTreeByTags.jsx";

const StyledTabs = withStyles((theme) => ({
  indicator: {
    backgroundColor: theme.palette.primary.dark,
    height: 8,
  },
}))((props) => <Tabs {...props} />);

const StyledTab = withStyles((theme) => ({
  root: {
    color: theme.palette.primary.dark,
    opacity: 1,
    fontSize: 15,
    fontWeight: 300,
    "&[disabled]": {
      display: "none",
    },
  },
  selected: {
    fontWeight: 900,
  },
}))((props) => <Tab {...props} />);

const useStyles = makeStyles((theme) => ({
  deckContentRoot: {
    margin: "32px 0 30px",
    minHeight: 700,
    height: "max-content",
    display: "flex",
    flexDirection: "column",
    position: "relative",
    [theme.breakpoints.up("lg")]: {
      margin: ({ openTree }) => (openTree ? "32px 0 32px 260px" : "32px auto"),
      width: 855,
    },
  },
  emptyDeck: {
    textAlign: "center",
    marginTop: 100,
  },
  questionIcon: {
    position: "fixed",
    left: 20,
    bottom: 20,
    backgroundColor: theme.palette.primary.main,
    width: 60,
    height: 60,
    [theme.breakpoints.up("lg")]: {
      left: 100,
      bottom: 30,
    },
    "&:hover": {
      backgroundColor: theme.palette.primary.main,
    },
  },
  questionImg: {
    width: 45,
    paddingLeft: "5px",
  },
  treeButton: {
    position: "absolute",
    width: 50,
    height: 50,
    minWidth: 50,
    top: 0,
    left: -50,
  },
}));

/** */
const ClassicDeckContent = () => {
  const t = useTranslate();
  const history = useHistory();
  const { currentDeck, exercisesTreeForDeck, saveCurrentBranch, branches } = useCourseProvider();
  const { rewards, exercise, resetRewards } = useLearnProcess();
  const { alert } = useDialogs();
  const wordId = useExtractRouteParamInt("/learn/:deckId/:wordId", "wordId");
  const exerciseId = useExtractRouteParamInt("/learn/:deckId/exercise/:exerciseId", "exerciseId");
  const [tabIndex, setTabIndex] = useState(0);
  const [missionRewards, setMissionRewards] = useState([]);

  const theoryLabel = IS_APP_BEHAPPY ? t("theoryAlt") : t("theory");
  // Элементы списка теории для дерева структуры (html + pdf-файлы)
  const theoryItems = makeTheoryTreeItems(currentDeck?.theory, theoryLabel);

  const treeMode = localStorage.getItem("tree_view_mode") || "open";
  const [openTree, setOpenTree] = useState(treeMode === "close" ? false : true);
  const classes = useStyles({ openTree });

  //сохранение режима отображения структуры упражнений при его изменении
  useEffect(() => {
    if (openTree) {
      localStorage.setItem("tree_view_mode", "open");
    } else {
      localStorage.setItem("tree_view_mode", "close");
    }
  }, [openTree]);

  // Эффект: при смене текущего урока или параметров маршрута выбираем стартовую вкладку
  // Логика: если в URL есть wordId — открываем вкладку 'Слова'; если есть exerciseId — 'Упражнения';
  // иначе выбираем первую доступную вкладку по приоритету: Видео -> Теория -> Слова -> Упражнения
  useEffectWithPreviousValues(
    /** @param {?Deck} prevDeck */
    ([prevDeck]) => {
      if (!currentDeck) {
        return;
      }
      if (prevDeck && prevDeck.id === currentDeck.id) {
        return;
      }
      // Если в URL указан wordId и в уроке есть слова (по items), переключаемся на вкладку "Слова"
      if (wordId && currentDeck.words.items.length > 0) {
        setTabIndex(2);
        return;
      }
      if (exerciseId && currentDeck.exercises.items.length > 0) {
        setTabIndex(3);
        return;
      }
      // Приоритетная вкладка по наличию контента: Видео -> Теория -> Слова -> Упражнения
      if (currentDeck.videos.items.length > 0) {
        setTabIndex(0);
      } else if (currentDeck.theory.html || currentDeck.theory.pdf) {
        setTabIndex(1);
      } else if (currentDeck.words.items.length > 0) {
        setTabIndex(2);
      } else if (currentDeck.exercises.items.length > 0) {
        setTabIndex(3);
      } else {
        setTabIndex(0);
      }
      //eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [currentDeck, wordId, exerciseId],
  );

  // Подписка на пользовательское событие для внешнего переключения вкладок обучения.
  // Это позволяет дочерним компонентам (например, Theory) без прямого доступа к локальному
  // состоянию родителя переключать активную вкладку.
  useEffect(() => {
    /** @param {CustomEvent} ev */
    const handler = (ev) => {
      const target = ev?.detail?.target;
      if (typeof target === "string") {
        switch (target) {
          case "videos":
            setTabIndex(0);
            break;
          case "theory":
            setTabIndex(1);
            break;
          case "words":
            setTabIndex(2);
            break;
          case "exercises":
            setTabIndex(3);
            break;
          default:
            break;
        }
      } else if (typeof target === "number") {
        setTabIndex(target);
      }
    };
    window.addEventListener("Learning.SwitchTab", handler);
    return () => window.removeEventListener("Learning.SwitchTab", handler);
  }, []);

  // Эффект: следит за изменениями наград из процесса обучения
  // Логика: из больших наград отфильтровывает элементы миссий (миссия или задача миссии) и сохраняет их в состояние
  // Отслеживание завершения урока
  useEffect(() => {
    if (rewards?.large) {
      if (rewards.large.items) {
        setMissionRewards(
          rewards.large.items.filter(
            (item) =>
              item.type === largeRewardItemTypes.learn_mission || item.type === largeRewardItemTypes.learn_mission_task,
          ),
        );
        if (!exercise?.deck?.exam) {
          const nextAction = rewards.large.nextAction;
          const learnedDeck = rewards.large.items?.find((item) => item.type === largeRewardItemTypes.learn_deck);
          if (learnedDeck) {
            alert({
              title: rewards.large.title,
              message: learnedDeck.content,
              // После успешного прохождения урока может прийти действие (nextAction) равное "none", если в настройках
              // предмета установлена опция "Не переходить на следующее упражнение" (retCurExOnAnswr). В таком случае текст
              // кнопки с поздравлением должен быть просто "OK"
              confirmText: nextAction !== "none" ? t("nextDeck") : "",
              cancelText: nextAction !== "none" ? t("Decks.stayHere") : "OK",
              onConfirm: () => congratCloseHandler(nextAction, rewards.large.nextId),
              onCancel: () => cancelCloseHandler(),
              disableEscape: true,
            });
          }
        }
      }
    }
  }, [rewards, exercise]);

  /** */
  const congratCloseHandler = (nextAction, nextActionDataId) => {
    //nextAction может быть "none" | "change_tab" | "change_deck" | "change_branch";
    // После успешного прохождения урока может прийти действие (nextAction) равное "none", если в настройках предмета
    // установлена опция "Не переходить на следующее упражнение" (retCurExOnAnswr).
    if (nextAction === "none") return;

    if (nextAction === "change_deck") {
      history.push(`/learn/${nextActionDataId}`);
    } else if (nextAction === "change_branch") {
      if (nextActionDataId) {
        const newBranch = branches.find((item) => item.id === nextActionDataId);
        newBranch && saveCurrentBranch(newBranch);
      }
    } else {
      history.push("/");
    }
    resetRewards(); // после того как пользователь прочитал поздравление, последнюю полученную "награду" надо сбросить
  };

  /** */
  const cancelCloseHandler = () => {
    resetRewards(); // после того как пользователь прочитал поздравление, последнюю полученную "награду" надо сбросить
  };

  // Эффект: показывает диалоги-поздравления на основе очереди missionRewards
  // Логика: берёт первую награду; если это миссия — показывает текст поздравления, если задача миссии — показывает
  // контент с нужным прогрессом; закрытие диалога переключает на следующую награду
  useEffect(() => {
    if (missionRewards.length > 0) {
      if (missionRewards[0].type === largeRewardItemTypes.learn_mission) {
        alert({
          confirmText: "OK",
          onConfirm: onCloseRewards,
          title: t("congratulation.mission", { name: missionRewards[0].content }),
          message: missionRewards[0].congratulationText,
          maxWidth: "md",
        });
      } else if (missionRewards[0].type === largeRewardItemTypes.learn_mission_task) {
        alert({
          confirmText: "OK",
          onConfirm: onCloseRewards,
          title: t("congratulation.missionTask.title", { name: missionRewards[0].content }),
          content: (
            <Typography>
              <span
                dangerouslySetInnerHTML={{
                  __html: t("congratulation.missionTask.content", { need: missionRewards[0].need }),
                }}
              />
            </Typography>
          ),
          maxWidth: "md",
        });
      }
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [missionRewards]);

  /** */
  const onCloseRewards = () => {
    const newRewards = _.cloneDeep(missionRewards);
    newRewards.shift();
    setMissionRewards(newRewards);
  };

  /**
   * @param {*} _
   * @param {Number} newValue
   */
  const onTabClick = (_, newValue) => {
    setTabIndex(newValue);
  };

  return (
    <Box className={classes.deckContentRoot}>
      <StyledTabs value={tabIndex} onChange={onTabClick} centered>
        {/* Вкладка Видео выключена, если массив items пустой */}
        <StyledTab label={t("videos")} disabled={currentDeck.videos.items.length === 0} />
        <StyledTab
          label={theoryLabel}
          disabled={!currentDeck.theory.html && !currentDeck.theory.pdf && !currentDeck.theory.additionalFileUrl}
        />
        {/* Вкладка Слова выключена, если массив items пустой */}
        <StyledTab label={t("words")} disabled={currentDeck.words.items.length === 0} />
        <StyledTab label={t("Exercises.tabTitle")} disabled={currentDeck.exercises.items.length === 0} />
      </StyledTabs>
      {tabIndex === 0 && <Videos />}
      {tabIndex === 1 &&
        (currentDeck.theory.html || currentDeck.theory.pdf || currentDeck.theory.additionalFileUrl) && <Theory />}
      {tabIndex === 1 &&
        !currentDeck.theory.html &&
        !currentDeck.theory.pdf &&
        !currentDeck.theory.additionalFileUrl && (
          <Typography className={classes.emptyDeck}>{t("emptyDeck")}</Typography>
        )}
      {tabIndex === 2 && <WordsLearning />}
      {tabIndex === 3 && <Exercises />}
      {(tabIndex === 3 || (tabIndex === 1 && theoryItems.length > 1)) &&
        (exercisesTreeForDeck.length > 0 || theoryItems.length > 1) &&
        !openTree && (
          <IconButton className={classes.treeButton} color="primary" onClick={() => setOpenTree(!openTree)}>
            <FormatIndentIncreaseIcon />
          </IconButton>
        )}
      {currentDeck.communicationLink && (
        <Tooltip title={t("askQuestion")} arrow>
          <IconButton className={classes.questionIcon} href={currentDeck.communicationLink} target="_blank">
            <img src={questionIcon} alt="question icon" className={classes.questionImg} />
          </IconButton>
        </Tooltip>
      )}
      {(exercisesTreeForDeck.length > 0 || theoryItems.length > 1) && (
        <ExercisesTreeByTags open={openTree} setOpen={setOpenTree} />
      )}
    </Box>
  );
};

export default ClassicDeckContent;
